import { ArrowLeft, ChefHat, HandPlatter, Link2, RefreshCw } from "lucide-react";
import { useRef } from "react";
import { toast } from "sonner";

import { COLUMNS } from "../lib/kds";
import { linkDaTela } from "../lib/telaKds";

import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LogoCentralCabecalho } from "@/features/cabecalho-logo";

/** O setor é DESTA tela, não da loja: fica guardado no aparelho, então o
 *  monitor do bar abre no bar mesmo que todos usem o login da loja. Lista
 *  suspensa no canto direito (pedido do dono): os chips ocupavam o meio do
 *  cabeçalho, onde agora fica a logo do sistema. */
function SetorTela({ setores, setorFilter, onSetor, largura = "w-[170px]" }) {
  if (!setores.length) return null;
  return (
    <div className="flex items-center gap-1.5 shrink-0">
      <span className="text-[11px] text-muted-foreground font-medium whitespace-nowrap">Esta tela:</span>
      <Select value={setorFilter} onValueChange={onSetor}>
        <SelectTrigger className={`h-9 ${largura} text-xs font-semibold`} data-e2e="kds-setor-tela">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="todos">Todas as praças</SelectItem>
          {setores.map((st) => <SelectItem key={st.id} value={st.id}>{st.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

/** Cozinha | Garçom — o papel deste aparelho. Guardado nele, como o setor. */
function ModoSwitch({ modo, onModo }) {
  const botao = (valor, Icone, rotulo) => (
    <button onClick={() => onModo(valor)} title={`Esta tela é da ${rotulo.toLowerCase()}`}
      className={`h-8 px-2.5 rounded-md text-xs font-bold flex items-center gap-1.5 transition-colors ${modo === valor ? "bg-orange-500 text-white shadow-sm" : "text-muted-foreground hover:bg-muted"}`}>
      <Icone className="w-3.5 h-3.5" />{rotulo}
    </button>
  );
  return (
    <div className="flex items-center gap-0.5 p-0.5 rounded-lg bg-muted/60 shrink-0">
      {botao("cozinha", ChefHat, "Cozinha")}
      {botao("garcom", HandPlatter, "Garçom")}
    </div>
  );
}

/** Cabeçalho do KDS. Desktop: uma linha (voltar, título, modo, setores,
 *  filtro de coluna, atualizar). Celular: título e modo em cima, e no modo
 *  cozinha as ABAS Produção/Prontos ocupando a largura toda — a aba substitui
 *  as duas colunas empilhadas, que dividiam a tela ao meio e escondiam o
 *  botão do cartão. No modo garçom as abas são da própria tela do garçom. */
export default function KdsHeader({
  desktop, filter, onFilter, mobileTab, onMobileTab, counts, pulseProducao,
  setores, setorFilter, onSetor, onRefresh, loading, onBack, modo, onModo,
  vistaGarcom = "todos", onVistaGarcom, contagemGarcom = {},
}) {
  const cozinha = modo !== "garcom";
  // A logo do sistema no centro, igual ao resto do painel; ela mede os dois
  // lados e se esconde sozinha quando não cabe.
  const esquerdaRef = useRef(null);
  const direitaRef = useRef(null);
  const copiarLink = async () => {
    const url = linkDaTela(cozinha ? setorFilter : "todos", modo, vistaGarcom);
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link desta tela copiado", { description: "Abra no aparelho da praça e deixe como atalho: ele já abre assim." });
    } catch (_) {
      toast.info(url, { description: "Copie o endereço acima.", duration: 12000 });
    }
  };

  const Titulo = (
    <div className="flex items-center gap-2 shrink-0">
      <button onClick={onBack} className="p-1.5 rounded-lg hover:bg-muted transition-colors" title="Voltar para Visão Geral">
        <ArrowLeft className="w-5 h-5 text-muted-foreground" />
      </button>
      <ChefHat className="w-6 h-6 text-orange-500" />
      <h1 className="text-lg sm:text-xl font-bold">KDS</h1>
    </div>
  );
  const Acoes = (
    <div className="flex items-center gap-1.5 shrink-0">
      <Button variant="outline" size="sm" onClick={copiarLink} className="h-9 w-9 p-0" title="Copiar o link desta tela pra deixar de atalho no aparelho">
        <Link2 className="w-4 h-4" />
      </Button>
      <Button variant="outline" size="sm" onClick={onRefresh} className="h-9 w-9 p-0" title="Atualizar">
        <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
      </Button>
    </div>
  );

  if (desktop) {
    const filtros = [
      { key: "todos", label: "Todos", count: counts.todos },
      { key: "producao", label: "Em Produção", count: counts.producao, cor: "bg-yellow-500" },
      { key: "pronto", label: "Prontos", count: counts.pronto, cor: "bg-green-500" },
    ];
    return (
      <div className="relative flex items-center gap-4 px-4 py-2 bg-card border-b flex-shrink-0">
        <div ref={esquerdaRef} className="flex items-center gap-4 shrink-0">
          {Titulo}
          <ModoSwitch modo={modo} onModo={onModo} />
        </div>
        <LogoCentralCabecalho esquerdaRef={esquerdaRef} direitaRef={direitaRef} />
        <div className="flex-1" />
        <div ref={direitaRef} className="flex items-center gap-1.5 shrink-0">
          {cozinha && <SetorTela setores={setores} setorFilter={setorFilter} onSetor={onSetor} />}
          {!cozinha && onVistaGarcom && [
            { key: "todos", label: "Todas", count: (contagemGarcom.paraLevar || 0) + (contagemGarcom.entregues || 0) },
            { key: "paraLevar", label: "Para levar", count: contagemGarcom.paraLevar, cor: "bg-emerald-600" },
            { key: "entregues", label: "Entregues", count: contagemGarcom.entregues, cor: "bg-zinc-500" },
          ].map((f) => (
            <button key={f.key} onClick={() => onVistaGarcom(f.key)} data-e2e={`garcom-vista-${f.key}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${vistaGarcom === f.key ? "bg-orange-500 text-white shadow" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}>
              {f.label}
              {f.count > 0 && (
                <span className={`ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-[10px] font-bold ${vistaGarcom === f.key ? "bg-white/25 text-white" : `${f.cor || "bg-muted-foreground/20"} text-white`}`}>{f.count}</span>
              )}
            </button>
          ))}
          {cozinha && filtros.map((f) => (
            <button key={f.key} onClick={() => onFilter(f.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filter === f.key ? "bg-orange-500 text-white shadow" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}>
              {f.label}
              {f.count > 0 && (
                <span className={`ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-[10px] font-bold ${filter === f.key ? "bg-white/25 text-white" : `${f.cor || "bg-muted-foreground/20"} text-white`}`}>{f.count}</span>
              )}
            </button>
          ))}
          {Acoes}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border-b flex-shrink-0">
      <div className="relative flex items-center justify-between gap-2 px-3 py-2">
        <div ref={esquerdaRef}>{Titulo}</div>
        <LogoCentralCabecalho esquerdaRef={esquerdaRef} direitaRef={direitaRef} />
        <div ref={direitaRef}>{Acoes}</div>
      </div>
      <div className="px-3 pb-2 flex items-center justify-between gap-2">
        <ModoSwitch modo={modo} onModo={onModo} />
        {cozinha && <SetorTela setores={setores} setorFilter={setorFilter} onSetor={onSetor} largura="w-[140px]" />}
      </div>
      {cozinha && (
        <div className="px-3 pb-2 grid grid-cols-2 gap-2">
          {COLUMNS.map((col) => {
            const ativa = mobileTab === col.status;
            const n = counts[col.status];
            const pulsa = col.status === "producao" && pulseProducao && !ativa;
            return (
              <button key={col.status} onClick={() => onMobileTab(col.status)}
                className={`h-11 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-colors ${ativa ? col.tabAtiva : "bg-muted text-muted-foreground"}`}>
                {col.label}
                {/* Sem o "0" antes da 1ª leitura chegar */}
                {!loading && <span className={`inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full text-xs font-bold ${ativa ? "bg-white/25 text-white" : `${col.headerBg} text-white`} ${pulsa ? "animate-bounce" : ""}`}>{n}</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
